/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';
import { NetworkManager } from '../../core/network/NetworkManager';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import {
    showResetAppOverlay,
    showSignOutOverlay,
} from './accountsoverlays/accountsoverlays.reducers';
import { AccountSettingsState } from './accountsettingsstate';
import {
    setIsMenuFocusedAction,
    setSubmenuVerticalPositionAction,
    setEnteredNumberAction,
    setNextHighlightPosition,
    setSubmenuSelectedItem,
    setMenuSelectedItem,
    setMenuHighlightItem,
    setMenuHighlightAction,
    setSubmenuHighlightAction,
} from './settings.actions';
import {
    SET_SETTINGS_MENU_FOCUS,
    SET_DEFAULT_SETTINGS_MENU_STATE,
    SET_SETTINGS_MENU_VERTICAL_POSITION,
    SET_DEFAULT_SETTINGS_SUBMENU_STATE,
    SET_SETTINGS_SUBMENU_FOCUS,
    SET_SETTINGS_SUBMENU_VERTICAL_POSITION,
    SET_SETTINGS_ENTERED_NUMBER,
    SET_SETTINGS_FOCUSED_ITEM_INDEX,
    SET_SETTINGS_MENU_SELECTED_ITEM,
    SET_SETTINGS_SUBMENU_SELECTED_ITEM,
    SET_SETTINGS_NEXT_HIGHLIGHT_POSITION,
    SET_SETTINGS_MENU_HIGHLIGHT_ITEM,
    SET_SETTINGS_HIGHLIGHT_MENU,
    SET_SETTINGS_HIGHLIGHT_SUBMENU,
} from './settings.types';
import { SettingsState } from './settingsstate';
import { showMenuLanguage } from './menulanguage/menulanguage.reducers';
import { setScreenTaskController } from '../screens/screens.reducers';
import { SettingsMenuTypes } from '../../views/SettingsScreen/SettingsMenuTypes';
import { CursorManager } from '../../core/navigation';
import {
    settingsVideoWindowDisableGhostMode,
    settingsVideoWindowEnableGhostMode,
    settingsVideoWindowShow,
} from './settingsvideowindow/settingsvideowindow.reducers';

const initialState: {
    isFocused: boolean;
    menuVerticalPosition: number;
    menuVerticalLimit: number;
    submenuVerticalPosition?: number;
    submenuVertialLimit: number;
    isSubmenuFocused: boolean;
    enteredNumber: string;
    focusedItemIndex: number;
} = {
    isFocused: true,
    menuVerticalPosition: 0,
    menuVerticalLimit: 0,
    submenuVertialLimit: 0,
    isSubmenuFocused: false,
    enteredNumber: '',
    focusedItemIndex: 0,
};

const settingsReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_DEFAULT_SETTINGS_MENU_STATE:
            return {
                ...state,
                menuVerticalLimit: payload.menuVerticalLimit,
                menuVerticalPosition: payload.menuVerticalPosition,
                isFocused: payload.isFocused,
                focusedItemIndex: payload.focusedItemIndex,
            };
        case SET_DEFAULT_SETTINGS_SUBMENU_STATE:
            return {
                ...state,
                submenuVertialLimit: payload.submenuVertialLimit,
                submenuVerticalPosition: payload.submenuVerticalPosition,
                isSubmenuFocused: payload.isSubmenuFocused,
            };
        case SET_SETTINGS_ENTERED_NUMBER:
            return {
                ...state,
                enteredNumber: payload.enteredNumber,
            };
        case SET_SETTINGS_FOCUSED_ITEM_INDEX:
            return {
                ...state,
                focusedItemIndex: payload.focusedItemIndex,
            };
        case SET_SETTINGS_HIGHLIGHT_MENU:
            return {
                ...state,
                focusedItemIndex: payload.focusedItemIndex,
                isFocused: payload.isFocused,
            };
        case SET_SETTINGS_HIGHLIGHT_SUBMENU:
            return {
                ...state,
                focusedItemIndex: payload.focusedItemIndex,
                isSubmenuFocused: payload.isSubmenuFocused,
            };
        case SET_SETTINGS_MENU_FOCUS:
            return {
                ...state,
                isFocused: payload.isFocused,
            };
        case SET_SETTINGS_MENU_HIGHLIGHT_ITEM:
            return {
                ...state,
                isFocused: payload.isFocused,
                isSubmenuFocused: payload.isSubmenuFocused,
                menuVerticalPosition: payload.menuVerticalPosition,
                submenuVerticalPosition: payload.submenuVerticalPosition,
            };
        case SET_SETTINGS_MENU_VERTICAL_POSITION:
            return {
                ...state,
                menuVerticalPosition: payload.menuVerticalPosition,
            };
        case SET_SETTINGS_MENU_SELECTED_ITEM:
            return {
                ...state,
                isFocused: payload.isFocused,
                focusedItemIndex: payload.focusedItemIndex,
                menuVerticalPosition: payload.menuVerticalPosition,
            };
        case SET_SETTINGS_NEXT_HIGHLIGHT_POSITION:
            return {
                ...state,
                focusedItemIndex: payload.focusedItemIndex,
                isFocused: payload.isFocused,
                isSubmenuFocused: payload.isSubmenuFocused,
            };
        case SET_SETTINGS_SUBMENU_FOCUS:
            return {
                ...state,
                isSubmenuFocused: payload.isSubmenuFocused,
            };
        case SET_SETTINGS_SUBMENU_SELECTED_ITEM:
            return {
                ...state,
                isSubmenuFocused: payload.isSubmenuFocused,
                focusedItemIndex: payload.focusedItemIndex,
                submenuVerticalPosition: payload.submenuVerticalPosition,
            };
        case SET_SETTINGS_SUBMENU_VERTICAL_POSITION:
            return {
                ...state,
                submenuVerticalPosition: payload.submenuVerticalPosition,
            };
        default:
            return state;
    }
};

export const highlightSubmenuScreens = () => (dispatch: Function, getState: Function) => {
    const { menuVerticalPosition } = getState().settings;
    if (
        menuVerticalPosition === SettingsState.USER_PREFERENCE ||
        menuVerticalPosition === SettingsState.ACCOUNTS
    ) {
        dispatch(setIsMenuFocusedAction(false));
        dispatch(setSubmenuSelectedItem(true, 0, 0));
    }
};

export const onKeydownSettingsOperations = () => (dispatch: Function, getState: Function) => {
    const { menuVerticalPosition, menuVerticalLimit } = getState().settings;
    const newVerticalPosition = menuVerticalPosition + 1;
    if (newVerticalPosition < menuVerticalLimit) {
        dispatch(setMenuSelectedItem(true, newVerticalPosition, newVerticalPosition));
    }
};

export const onKeyDownSettingsSubmenuOperations =
    () => (dispatch: Function, getState: Function) => {
        const { submenuVerticalPosition, submenuVertialLimit } = getState().settings;
        const newVerticalPosition = submenuVerticalPosition + 1;
        if (newVerticalPosition < submenuVertialLimit) {
            dispatch(setSubmenuSelectedItem(true, newVerticalPosition, newVerticalPosition));
        }
    };

export const onKeyOkAccountSettingsOperations = () => (dispatch: Function, getState: Function) => {
    const { submenuVerticalPosition } = getState().settings;
    switch (submenuVerticalPosition) {
        case AccountSettingsState.RESET_APP:
            dispatch(showResetAppOverlay());
            break;
        case AccountSettingsState.SIGN_OUT:
            if (!NetworkManager.isNetworkDisconnected()){
              dispatch(showSignOutOverlay());
            };
            break;

        default:
    }
};

export const onKeyOkUserPreferenceSettings = () => (dispatch: Function, getState: Function) => {
    const { submenuVerticalPosition } = getState().settings;
    if (submenuVerticalPosition === 0) {
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_MENU_LANGUAGE_TASKS));
        dispatch(showMenuLanguage());
    } else {
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_VIDEO_WINDOW_TASKS));
        dispatch(settingsVideoWindowShow());
    }
};

export const onKeyUpSettingsOperations = () => (dispatch: Function, getState: Function) => {
    const { enteredNumber, menuVerticalPosition } = getState().settings;
    const newVerticalPosition = menuVerticalPosition - 1;
    if (menuVerticalPosition > 0) {
        dispatch(setMenuSelectedItem(true, newVerticalPosition, newVerticalPosition));
    }
    if (enteredNumber) {
        dispatch(setEnteredNumberAction(''));
    }
};

export const onKeyUpSettingsSubmenuOperations = () => (dispatch: Function, getState: Function) => {
    const { submenuVerticalPosition } = getState().settings;
    const newVerticalPosition = submenuVerticalPosition - 1;
    if (submenuVerticalPosition > 0) {
        dispatch(setSubmenuSelectedItem(true, newVerticalPosition, newVerticalPosition));
    }
};

export const onMouseClickSettingsOperations = (focusItemIndex) => (dispatch: Function) => {
    dispatch(setMenuHighlightItem(true, false, focusItemIndex));
};

export const onMouseOutSettingsOperations = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const menuItem = value.split('-');
    const listType = menuItem[1];
    if (listType === SettingsMenuTypes.MENU) {
        dispatch(setMenuHighlightAction(false));
    } else {
        dispatch(setSubmenuHighlightAction(false));
    }
};

export const onMouseOverSettingsOperations = (focusItemIndex, listType) => (dispatch: Function) => {
    if (listType === SettingsMenuTypes.MENU) {
        dispatch(setNextHighlightPosition(focusItemIndex, true, false));
    } else {
        dispatch(setSubmenuVerticalPositionAction(focusItemIndex));
        dispatch(setNextHighlightPosition(focusItemIndex, false, true));
        dispatch(setSubmenuTasksController());
    }
};

export const setSubmenuTasksController = () => (dispatch: Function, getState: Function) => {
    const { menuVerticalPosition } = getState().settings;
    switch (menuVerticalPosition) {
        case SettingsState.ACCOUNTS:
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_ACCOUNT_TASKS));
            break;
        case SettingsState.USER_PREFERENCE:
            dispatch(
                setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_USER_PREFERENCE_TASKS),
            );
            break;
        default:
    }
};

export const onCursorStateChangeSettingsOperations =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        const { menuVerticalPosition, submenuVerticalPosition } = getState().settings;
        const { verticalPosition } = getState().menulanguage;
        switch (screenTaskController) {
            case ScreenTaskControllerTypes.DEFAULT_SETTING_TASKS:
                if (CursorManager.isCursorAppeared()) {
                    dispatch(setMenuHighlightAction(false, menuVerticalPosition));
                } else {
                    dispatch(setMenuHighlightAction(true, menuVerticalPosition));
                }
                break;
            case ScreenTaskControllerTypes.SETTINGS_MENU_LANGUAGE_TASKS:
                if (CursorManager.isCursorAppeared()) {
                    dispatch(setMenuHighlightAction(false, verticalPosition));
                } else {
                    dispatch(setMenuHighlightAction(true, verticalPosition));
                }
                break;
            case ScreenTaskControllerTypes.SETTINGS_ACCOUNT_TASKS:
            case ScreenTaskControllerTypes.SETTINGS_USER_PREFERENCE_TASKS:
                if (CursorManager.isCursorAppeared()) {
                    dispatch(setSubmenuHighlightAction(false, submenuVerticalPosition));
                } else {
                    dispatch(setNextHighlightPosition(submenuVerticalPosition, false, true));
                }
                break;
            case ScreenTaskControllerTypes.SETTINGS_VIDEO_WINDOW_TASKS:
                if (CursorManager.isCursorAppeared()) {
                    dispatch(settingsVideoWindowEnableGhostMode());
                } else {
                    dispatch(settingsVideoWindowDisableGhostMode());
                }
                break;

            default:
        }
    };

export default settingsReducer;
